import { useCallback, useMemo } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/machines/api/config.ts";
import { getTruckEventsOverview } from "@/machines/api/requests/events.requests.ts";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import type { ApiResponse } from "@/shared/types/api.ts";
import type { TruckEventOverview } from "@/machines/types/event.ts";
import type { Machine } from "@/machines/types/machine.ts";

export interface MachineKpi {
  /** Average truck-loading cycle time, in seconds. */
  avgCycleTime?: number;
  /** Cycle time vs target (backend-provided, seconds delta). */
  vsTarget?: number;
  targetCycleTime?: number;
  isLoading: boolean;
}

/**
 * Page size shared with `useAggregatedMetadata` (the KPIs panel / location KPI
 * stripe). Both hooks read ONLY `data.metadata`, never the paginated payload, so
 * the size is free to be identical — and it has to be, because an identical key
 * with a different request would serve one hook the other's page. 3000 is the
 * value every other overview consumer already uses (location-detail,
 * machines-live), so nothing loses data by unifying on it.
 */
const OVERVIEW_PAGE_SIZE = 3000;

/**
 * Per-machine headline KPIs for the map rail cards — runs one
 * `getTruckEventsOverview` query per machine, keyed identically to
 * `useAggregatedMetadata` (same params AND same page size) so opening the KPIs
 * panel is served from these cache entries instead of re-fetching the same
 * endpoint per machine under a second key. Returns a map keyed by `device_mxid`.
 */
export const useMachineKpis = (machines: Machine[]): Record<string, MachineKpi> => {
  const { value } = useDateTimeRangeStore();
  const deviceMxids = useMemo(
    () => machines.map((m) => m.device_mxid).filter(Boolean),
    [machines],
  );

  // Build the keyed map inside `combine` (stable ref via `useCallback`) so the
  // returned object keeps a stable reference when the query results are
  // unchanged — query-core memoizes it via `replaceEqualDeep`, unlike a trailing
  // `useMemo` fed the fresh array `useQueries` returns every render.
  const combine = useCallback(
    (results: UseQueryResult<ApiResponse<TruckEventOverview>>[]): Record<string, MachineKpi> => {
      const map: Record<string, MachineKpi> = {};
      deviceMxids.forEach((mxid, i) => {
        const q = results[i];
        const tl = q?.data?.data?.metadata?.truckLoading;
        map[mxid] = {
          avgCycleTime: tl?.avgCycleTime,
          vsTarget: tl?.vsTarget,
          targetCycleTime: tl?.targetCycleTime,
          isLoading: q?.status === "pending",
        };
      });
      return map;
    },
    [deviceMxids],
  );

  return useQueries({
    queries: deviceMxids.map((device_mxid) => ({
      queryKey: [
        QUERY_KEYS.TRUCK_EVENTS_OVERVIEW,
        {
          device_mxid,
          start_time: value.fromDate,
          end_time: value.toDate,
          page: 1,
          itemsPerPage: OVERVIEW_PAGE_SIZE,
        },
      ],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        getTruckEventsOverview(
          {
            device_mxid,
            start_time: value.fromDate,
            end_time: value.toDate,
            page: 1,
            itemsPerPage: OVERVIEW_PAGE_SIZE,
          },
          signal,
        ),
      enabled: !!device_mxid,
    })),
    combine,
  });
};
